import { cn } from '@/lib/utils'
import { textLinkClass } from '@/components/ui/text-link'
import { useState } from 'react'
import { toast } from 'sonner'
import { faCircleExclamation } from '@awesome.me/kit-e215ba4646/icons/sharp/light'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
} from '@/components/ui/responsive-dialog'
import { InvitationMessages as M } from '@/constants/messages'
import { contactInvitationAdmin } from '@/services/auth/invitationContact'

/**
 * The accept page's answer for an invitation it can't open (already accepted,
 * expired or cancelled; the rules hide those from signed-out visitors). "Contact
 * your admin" sends a fixed notification to the admin, no typed message (D83).
 * Without an invitation id in the link there's nobody to tell, so no link.
 */
export function InvitationClosedNotice({
  invitationId,
  onSignIn,
}: {
  invitationId: string | null
  onSignIn: () => void
}) {
  const [open, setOpen] = useState(false)
  const [sending, setSending] = useState(false)

  const send = async () => {
    if (!invitationId) return
    setSending(true)
    const ok = await contactInvitationAdmin(invitationId)
    setSending(false)
    if (ok) {
      setOpen(false)
      toast.success(M.contactSent)
    } else {
      toast.error(M.contactFailed)
    }
  }

  return (
    <>
      <Alert variant="destructive">
        <Icon icon={faCircleExclamation} className="h-4 w-4" />
        <AlertTitle>{M.closedTitle}</AlertTitle>
        <AlertDescription>
          <p>
            {M.closedBefore}
            {invitationId ? (
              <button
                type="button"
                onClick={() => setOpen(true)}
                className={cn('inline text-left', textLinkClass)}
              >
                {M.closedLink}
              </button>
            ) : (
              M.closedLink
            )}
            {M.closedAfter}
          </p>
        </AlertDescription>
      </Alert>
      <Button onClick={onSignIn} variant="outline" className="w-full">
        {M.closedSignIn}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{M.contactTitle}</DialogTitle>
            <DialogDescription>{M.contactText}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={sending}
            >
              {M.contactCancel}
            </Button>
            <Button onClick={send} disabled={sending}>
              {M.contactSend}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
